// /admin/reservations — table reservations and the walk-in waitlist
// (F-RSV-N1 / F-RSV-N2). A day view of bookings grouped by time with table
// chips; approve online requests; seat (opens the table's tab), no-show,
// cancel, edit; WhatsApp confirmation click-out. Off until an admin switches
// reservations on in Settings.
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, ChevronLeft, ChevronRight, MessageCircle, MoreHorizontal, Plus, Users, X } from 'lucide-react'
import apiClient from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { DatePicker } from '@/components/ui/date-picker'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { useAllSettings } from '@/hooks/useKitchenFlowEnabled'
import { useReservationsConfig } from '@/hooks/useReservations'
import { cn } from '@/lib/utils'
import type { DiningTable } from '@/types'
import {
  SOURCE_LABEL,
  STATUS_LABEL,
  addDaysISO,
  businessTodayISO,
  canMarkNoShow,
  confirmationMessage,
  groupBySlot,
  isActiveReservation,
  pktDate,
  pktTime,
  restaurantNameFrom,
  waLink,
  type Reservation,
  type ReservationStatus,
} from '@/lib/reservations'
import { BookingDialog } from './BookingDialog'
import { SeatDialog, type SeatTarget } from './SeatDialog'
import { WaitlistTab } from './WaitlistTab'

type Tab = 'bookings' | 'waitlist'

const STATUS_VARIANT: Record<ReservationStatus, 'warning-surface' | 'info-surface' | 'success-surface' | 'neutral-surface' | 'danger-surface'> = {
  pending: 'warning-surface',
  confirmed: 'info-surface',
  seated: 'success-surface',
  completed: 'neutral-surface',
  cancelled: 'neutral-surface',
  no_show: 'danger-surface',
}

export function ReservationsPage() {
  const { enabled, isLoading } = useReservationsConfig()
  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>
  if (!enabled) {
    return (
      <div className="space-y-2 p-4 sm:p-6">
        <h1 className="text-3xl font-bold tracking-tight">Reservations</h1>
        <p className="text-muted-foreground">
          Reservations are switched off. An admin can turn them on in Settings → General → Table reservations.
        </p>
      </div>
    )
  }
  return <ReservationsScreen />
}

function useRestaurantName(): string {
  return restaurantNameFrom(useAllSettings())
}

function ReservationsScreen() {
  const { config } = useReservationsConfig()
  const all = useAllSettings()
  const boundary = typeof all?.day_boundary_hour === 'number' ? (all.day_boundary_hour as number) : 5
  const today = businessTodayISO(new Date(), boundary)
  const [tab, setTab] = useState<Tab>('bookings')
  const [date, setDate] = useState(today)
  const [editing, setEditing] = useState<Reservation | null | 'new'>(null)
  const [seating, setSeating] = useState<SeatTarget | null>(null)
  const [cancelling, setCancelling] = useState<Reservation | null>(null)
  const restaurantName = useRestaurantName()

  const { data: tables = [] } = useQuery({
    queryKey: ['tables', 'reservations'],
    queryFn: () => apiClient.getTables().then((r) => (r.data ?? []) as DiningTable[]),
    staleTime: 30_000,
  })
  const { data: pending } = useQuery({
    queryKey: ['reservations', 'pending'],
    queryFn: () => apiClient.listPendingReservations().then((r) => r.data?.reservations ?? []),
    refetchInterval: 30_000,
  })

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reservations</h1>
          <p className="mt-1 text-muted-foreground">Bookings by day, online requests to approve, and the walk-in waitlist.</p>
        </div>
        {tab === 'bookings' ? (
          <Button onClick={() => setEditing('new')}>
            <Plus className="mr-2 h-4 w-4" /> New booking
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-1 rounded-lg bg-muted/60 p-1 w-fit">
        {(
          [
            ['bookings', 'Bookings'],
            ['waitlist', 'Walk-in waitlist'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={cn(
              'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
              tab === id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {label}
            {id === 'bookings' && (pending?.length ?? 0) > 0 ? (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1.5 text-[11px] font-semibold leading-none text-white">
                {pending!.length}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {tab === 'bookings' ? (
        <>
          {(pending?.length ?? 0) > 0 ? (
            <PendingPanel
              list={pending!}
              onEdit={(r) => setEditing(r)}
              onCancel={(r) => setCancelling(r)}
              onShowDay={(d) => setDate(d)}
            />
          ) : null}
          <DayView
            date={date}
            today={today}
            setDate={setDate}
            graceMinutes={config.no_show_grace_minutes}
            restaurantName={restaurantName}
            onEdit={(r) => setEditing(r)}
            onSeat={(r) =>
              setSeating({
                kind: 'reservation',
                id: r.id,
                guestName: r.guest_name,
                guestPhone: r.guest_phone,
                partySize: r.party_size,
                notes: r.notes,
                tableIds: r.tables.map((t) => t.id),
              })
            }
            onCancel={(r) => setCancelling(r)}
          />
        </>
      ) : (
        <WaitlistTab tables={tables} restaurantName={restaurantName} />
      )}

      <BookingDialog
        open={editing !== null}
        onOpenChange={(o) => !o && setEditing(null)}
        booking={editing === 'new' ? null : editing}
        defaultDate={date}
        defaultDuration={config.default_duration_minutes}
        tables={tables}
        onSaved={(r) => setDate(businessTodayISO(new Date(r.starts_at), boundary))}
      />
      <SeatDialog target={seating} tables={tables} onOpenChange={(o) => !o && setSeating(null)} />
      <CancelDialog booking={cancelling} onOpenChange={(o) => !o && setCancelling(null)} />
    </div>
  )
}

function useReservationAction() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: async ({ id, action, reason }: { id: string; action: 'confirm' | 'no-show' | 'complete' | 'cancel'; reason?: string }) => {
      const res = await apiClient.reservationAction(id, action, reason ? { reason } : undefined)
      if (!res.success || !res.data) throw new Error(res.message || 'Could not update the booking')
      return res.data
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['reservations'] })
      toast({ title: `${r.guest_name}: ${STATUS_LABEL[r.status]}` })
    },
    onError: (e: Error) => toast({ title: 'Could not update', description: e.message, variant: 'destructive' }),
  })
}

function PendingPanel({
  list,
  onEdit,
  onCancel,
  onShowDay,
}: {
  list: Reservation[]
  onEdit: (r: Reservation) => void
  onCancel: (r: Reservation) => void
  onShowDay: (d: string) => void
}) {
  const act = useReservationAction()
  return (
    <Card className="rounded-lg border-warning-border">
      <CardContent className="space-y-3 p-4">
        <div>
          <h2 className="text-base font-semibold">Online requests to approve</h2>
          <p className="text-sm text-muted-foreground">These guests booked on your website and are waiting for a yes.</p>
        </div>
        <div className="divide-y rounded-lg border">
          {list.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm">
              <div className="min-w-0">
                <button type="button" className="font-medium hover:underline" onClick={() => onShowDay(businessTodayISO(new Date(r.starts_at)))}>
                  {pktDate(r.starts_at)} · {pktTime(r.starts_at)}
                </button>
                <span className="ml-2">{r.guest_name}</span>
                <span className="ml-2 text-muted-foreground">
                  {r.party_size} guests{r.tables.length ? ` · ${r.tables.map((t) => t.table_number).join(' + ')}` : ''}
                  {r.guest_phone ? ` · ${r.guest_phone}` : ''}
                </span>
                {r.notes ? <p className="text-muted-foreground">{r.notes}</p> : null}
              </div>
              <div className="flex gap-2">
                <Button size="sm" disabled={act.isPending} onClick={() => act.mutate({ id: r.id, action: 'confirm' })}>
                  <Check className="mr-2 h-4 w-4" /> Approve
                </Button>
                <Button size="sm" variant="outline" onClick={() => onEdit(r)}>
                  Edit
                </Button>
                <Button size="sm" variant="ghost" onClick={() => onCancel(r)}>
                  <X className="mr-2 h-4 w-4" /> Decline
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function DayView({
  date,
  today,
  setDate,
  graceMinutes,
  restaurantName,
  onEdit,
  onSeat,
  onCancel,
}: {
  date: string
  today: string
  setDate: (d: string) => void
  graceMinutes: number
  restaurantName: string
  onEdit: (r: Reservation) => void
  onSeat: (r: Reservation) => void
  onCancel: (r: Reservation) => void
}) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['reservations', 'day', date],
    queryFn: () => apiClient.listReservations(date).then((r) => r.data?.reservations ?? []),
    refetchInterval: 30_000,
  })
  const list = data ?? []
  const active = useMemo(() => list.filter(isActiveReservation), [list])
  const closed = useMemo(() => list.filter((r) => !isActiveReservation(r)), [list])
  const groups = useMemo(() => groupBySlot(active), [active])
  const covers = active.reduce((s, r) => s + r.party_size, 0)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" aria-label="Previous day" onClick={() => setDate(addDaysISO(date, -1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <DatePicker value={date} onChange={(v) => v && setDate(v)} className="w-44" />
          <Button variant="outline" size="sm" aria-label="Next day" onClick={() => setDate(addDaysISO(date, 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          {date !== today ? (
            <Button variant="ghost" size="sm" onClick={() => setDate(today)}>
              Today
            </Button>
          ) : null}
        </div>
        <p className="text-sm text-muted-foreground">
          {active.length} {active.length === 1 ? 'booking' : 'bookings'} · {covers} {covers === 1 ? 'guest' : 'guests'}
        </p>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : isError ? (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">Could not load bookings.</p>
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      ) : groups.length === 0 ? (
        <Card className="rounded-lg">
          <CardContent className="p-8 text-center text-sm text-muted-foreground">{closed.length ? 'No upcoming bookings left for this day.' : 'No bookings for this day yet.'}</CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.key} className="space-y-2">
              <h3 className="text-sm font-semibold text-muted-foreground">{g.label}</h3>
              {g.items.map((r) => (
                <ReservationRow
                  key={r.id}
                  r={r}
                  graceMinutes={graceMinutes}
                  restaurantName={restaurantName}
                  onEdit={onEdit}
                  onSeat={onSeat}
                  onCancel={onCancel}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {closed.length > 0 ? (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground">Finished, cancelled and no-shows</h3>
          <div className="divide-y rounded-lg border">
            {closed.map((r) => (
              <ClosedRow key={r.id} r={r} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function ClosedRow({ r }: { r: Reservation }) {
  const act = useReservationAction()
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm">
      <span>
        <span className="text-muted-foreground">{pktTime(r.starts_at)}</span> · {r.guest_name}{' '}
        <span className="text-muted-foreground">({r.party_size})</span>
        {r.tables.length ? <span className="text-muted-foreground"> · {r.tables.map((t) => t.table_number).join(' + ')}</span> : null}
        {r.cancel_reason ? <span className="text-muted-foreground"> · {r.cancel_reason}</span> : null}
      </span>
      <span className="flex items-center gap-2">
        <Badge variant={STATUS_VARIANT[r.status]}>{STATUS_LABEL[r.status]}</Badge>
        {r.status === 'no_show' ? (
          <Button size="sm" variant="ghost" disabled={act.isPending} onClick={() => act.mutate({ id: r.id, action: 'confirm' })}>
            Undo no-show
          </Button>
        ) : null}
      </span>
    </div>
  )
}

function ReservationRow({
  r,
  graceMinutes,
  restaurantName,
  onEdit,
  onSeat,
  onCancel,
}: {
  r: Reservation
  graceMinutes: number
  restaurantName: string
  onEdit: (r: Reservation) => void
  onSeat: (r: Reservation) => void
  onCancel: (r: Reservation) => void
}) {
  const act = useReservationAction()
  const qc = useQueryClient()
  const { toast } = useToast()
  const noShowOk = canMarkNoShow(r, graceMinutes)
  const href = waLink(r.guest_phone, confirmationMessage(r, restaurantName))

  const sendConfirmation = () => {
    if (!href) return
    window.open(href, '_blank', 'noopener')
    apiClient
      .markReservationConfirmationSent(r.id)
      .then(() => qc.invalidateQueries({ queryKey: ['reservations'] }))
      .catch(() => toast({ title: 'Opened WhatsApp', description: 'Could not mark the confirmation as sent.' }))
  }

  return (
    <Card className="rounded-lg">
      <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{r.guest_name}</span>
            <span className="flex items-center gap-1 text-sm text-muted-foreground">
              <Users className="h-4 w-4" aria-hidden /> {r.party_size}
            </span>
            {r.tables.length ? (
              r.tables.map((t) => (
                <Badge key={t.id} variant="outline">
                  {t.table_number}
                </Badge>
              ))
            ) : (
              <Badge variant="outline" className="text-muted-foreground">
                No table
              </Badge>
            )}
            <Badge variant={STATUS_VARIANT[r.status]}>{STATUS_LABEL[r.status]}</Badge>
            {r.capacity_override ? <Badge variant="warning-surface">Over capacity</Badge> : null}
          </div>
          <p className="text-sm text-muted-foreground">
            {pktTime(r.starts_at)}–{pktTime(r.ends_at)} · {SOURCE_LABEL[r.source]}
            {r.guest_phone ? ` · ${r.guest_phone}` : ''}
            {r.order_number ? ` · order ${r.order_number}` : ''}
            {r.confirmation_sent_at ? ' · confirmation sent' : ''}
          </p>
          {r.notes ? <p className="text-sm text-muted-foreground">{r.notes}</p> : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {r.status === 'pending' ? (
            <Button size="sm" disabled={act.isPending} onClick={() => act.mutate({ id: r.id, action: 'confirm' })}>
              <Check className="mr-2 h-4 w-4" /> Approve
            </Button>
          ) : null}
          {r.status === 'confirmed' ? (
            <Button size="sm" onClick={() => onSeat(r)}>
              Seat
            </Button>
          ) : null}
          {r.status === 'seated' ? (
            <Button size="sm" variant="outline" disabled={act.isPending} onClick={() => act.mutate({ id: r.id, action: 'complete' })}>
              Finish
            </Button>
          ) : null}
          {(r.status === 'confirmed' || r.status === 'pending') && href ? (
            <Button size="sm" variant="outline" onClick={sendConfirmation}>
              <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp
            </Button>
          ) : null}
          {r.status === 'confirmed' || r.status === 'pending' ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="ghost" aria-label={`More actions for ${r.guest_name}`}>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => onEdit(r)}>Edit booking</DropdownMenuItem>
                {r.status === 'confirmed' ? (
                  <DropdownMenuItem
                    disabled={!noShowOk || act.isPending}
                    onSelect={() => act.mutate({ id: r.id, action: 'no-show' })}
                  >
                    {noShowOk ? 'Mark no-show' : `No-show after ${graceMinutes} min late`}
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-destructive" onSelect={() => onCancel(r)}>
                  {r.status === 'pending' ? 'Decline request' : 'Cancel booking'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}

function CancelDialog({ booking, onOpenChange }: { booking: Reservation | null; onOpenChange: (o: boolean) => void }) {
  const act = useReservationAction()
  const [reason, setReason] = useState('')
  return (
    <Dialog
      open={Boolean(booking)}
      onOpenChange={(o) => {
        if (!o) setReason('')
        onOpenChange(o)
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{booking?.status === 'pending' ? 'Decline this request?' : 'Cancel this booking?'}</DialogTitle>
          <DialogDescription>
            {booking ? `${booking.guest_name} · ${booking.party_size} guests · ${pktDate(booking.starts_at)} ${pktTime(booking.starts_at)}` : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="resv-cancel-reason">Reason (optional)</Label>
          <Input id="resv-cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Guest called to cancel" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Keep it
          </Button>
          <Button
            variant="destructive"
            disabled={act.isPending}
            onClick={() => {
              if (!booking) return
              act.mutate(
                { id: booking.id, action: 'cancel', reason: reason.trim() || undefined },
                {
                  onSuccess: () => {
                    setReason('')
                    onOpenChange(false)
                  },
                },
              )
            }}
          >
            {booking?.status === 'pending' ? 'Decline' : 'Cancel booking'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
